/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { NextRouter } from "next/router";
import StateInterface from "../../../redux-magic/state-interface";
import { setBroadcastFormThunk } from "../../../redux-magic/store";

/* Component Imports */

import { Slider, Typography } from "@mui/material";

/* Icon Imports */

import { EmojiObjectsOutlined } from "@mui/icons-material";

/* Styled Components */

const Container = styled("div")({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	backgroundImage: "url('/images/broadcast/BuildingBackground.svg')",
	backgroundRepeat: "no-repeat",
	backgroundPosition: "center",
	backgroundSize: "cover",
	width: "100%",
	height: "100%",
	minHeight: "100%",
	flex: 1,
	position: "relative",
	padding: "1rem",
});

const Heading = styled(Typography)({
	fontWeight: 700,
	textAlign: "center",
	margin: "3rem 0rem 0rem 0rem",
});

const Brokerage = styled(Typography)({
	fontWeight: 700,
	textAlign: "center",
	color: "#5B5B5B",
	margin: "3rem 0rem 0rem 0rem",
});

const RangeSlider = styled(Slider)({
	width: "80%",
	height: "5px",
	margin: "2.5rem 0rem 1rem 0rem",
	"& .MuiSlider-track": {
		color: "#621B16",
	},
	"& .MuiSlider-thumb": {
		color: "#621B16",
	},
	"& .MuiSlider-rail": {
		color: "#828282",
	},
	"& .MuiSlider-valueLabel": {
		lineHeight: 1.2,
		fontSize: 14,
		background: "unset",
		padding: 0,
		width: "4rem",
		height: "2rem",
		borderRadius: "0.5rem",
		backgroundColor: "#ffe9d9",
		color: "#5f2b01",
	},
});

const TipContainer = styled("div")({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "flex-start",
	borderRadius: "1rem",
	background: "#FFF3E0",
	gap: "0.5rem",
	padding: "1rem",
	margin: "3rem 0rem 2rem 0rem",
});

const Tip = styled(Typography)({
	fontWeight: 400,
	fontSize: "0.75rem",
});

//rental brokarage values
const rentMarks = [
	{
		value: 10,
		label: '10',
	},
	{
		value: 15,
		label: '15',
	},
	{
		value: 30,
		label: '30',
	},
	{
		value: 45,
		label: '45',
	},
	{
		value: 90,
		label: '90',
	},
];

export default function Commission({
	dispatch,
	broadcast,
	transactionType,
	router,
}: {
	dispatch: Function;
	broadcast: StateInterface["broadcast_data"] | undefined | null;
	transactionType: string;
	router: NextRouter;
}) {
	/* Translations */

	const localLang = {
		how_much_brokerage_are_you_offering:
			router.locale === "ar-AE"
				? "كم عمولة تقدمها؟"
				: router.locale === "kn-IN"
					? "ನೀವು ಎಷ್ಟು ಬ್ರೋಕರೇಜ್ ನೀಡುತ್ತಿದ್ದೀರಿ?"
					: "How much brokerage are you willing to offer?",
		days: router.locale === "ar-AE" ? "يوم " : router.locale === "kn-IN" ? " ದಿನಗಳು" : " days",
		tip_content:
			router.locale === "ar-AE"
				? "السمسرة هي الجزء الأكثر أهمية في الصفقة، فاستهدف أفضل نسبة يمكن للطرفين الاتفاق عليها براحة"
				: router.locale === "kn-IN"
					? "ಬ್ರೋಕರೆಜ್ ಒಪ್ಪಂದದ ಅತ್ಯಂತ ಪ್ರಮುಖ ಭಾಗವಾಗಿದೆ, ಎರಡೂ ಪಾರ್ಟಿಗಳು ಆರಾಮವಾಗಿ ಒಪ್ಪಿಗೆ ನೀಡಬಹುದಾದ ಉತ್ತಮ ದರವನ್ನು ಗುರಿಯಾಗಿಡಿ"
					: "Brokerage is the most important part of the deal, aim for the best rate that both sides can agree on",
	};

	const [brokerage, setBrokerage] = React.useState<number>(broadcast ? broadcast.commission_offered : 0);

	React.useEffect(() => {
		if (!broadcast?.commission_offered || broadcast?.commission_offered === 0) {
			setBrokerage(transactionType === "rent" ? 15 : 2);

			dispatch(
				setBroadcastFormThunk({
					commission_offered: transactionType === "rent" ? 15 : 2,
					no_of_agents: broadcast?.no_of_agents || 0,
					notes: broadcast?.notes || "",
				}),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Container>
			{/* Heading */}

			<Heading variant="h5">{localLang.how_much_brokerage_are_you_offering}</Heading>

			{/* Brokerage */}

			<Brokerage variant="h1">{brokerage + (transactionType === "rent" ? localLang.days : "%")}</Brokerage>

			{/* Slider */}

			<RangeSlider
				value={brokerage}
				step={transactionType === "rent" ? null : 0.5}
				min={transactionType === "rent" ? 10 : 0.5}
				max={transactionType === "rent" ? 90 : 10}
				valueLabelDisplay="auto"
				{...transactionType === "rent"? {marks: rentMarks} : {}}
				onChange={(_event, value) => {
					const numValue = Array.isArray(value) ? value[0] : value;
					setBrokerage(numValue as number);
				}}
				onChangeCommitted={(_event, value) => {
					const numValue = Array.isArray(value) ? value[0] : value;
					dispatch(
						setBroadcastFormThunk({
							commission_offered: numValue || 0,
							no_of_agents: broadcast?.no_of_agents || 0,
							notes: broadcast?.notes || "",
						}),
					);
				}}
			/>

			{/* Tip */}

			<TipContainer>
				<EmojiObjectsOutlined
					sx={{
						color: "#FFB800",
						fontSize: "1.5rem",
					}}
				/>
				<Tip variant="body1">{localLang.tip_content}</Tip>
			</TipContainer>
		</Container>
	);
}
